/***
 *
 *   PRODUCT EVENTS / DETAIL
 *   Every occurrence of one event in the selected date range, one column per
 *   metadata key — e.g. the `reason` behind each account_closed (CORE-4277).
 *
 **********/

import { useMemo, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Animate, DataTable, Icon, useAPI } from 'components/lib';
import { convertToLocalTime } from 'utility/helper';
import Style from './tailwind';
import { DETAIL_BASE_COLUMNS, MSG, PAGE_SIZES } from './constants';
import { DateRangeSelector, rangeFromSearch, rangeToSearch } from './dateRange';
import { flattenMetadata, isMetadataColumn, metadataColumns } from './metadata';

const LONG_VALUE = 24;

function buildListUrl(name, range, page, pageSize) {
  const params = new URLSearchParams({
    name,
    start_date: range.start,
    end_date:   range.end,
    limit:      String(pageSize),
    offset:     String((page - 1) * pageSize),
  });
  return `/api/product-events/list?${params}`;
}

// keyed on the name so paging state never carries from one event to another
export function ProductEventDetail() {
  const { name } = useParams();
  return <EventOccurrences key={name} name={name} />;
}

function EventOccurrences({ name }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateRange = useMemo(() => rangeFromSearch(searchParams), [searchParams]);

  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  const list    = useAPI(buildListUrl(name, dateRange, page, pageSize));
  const results = list.data?.results;
  const total   = list.data?.total ?? 0;

  const columns = useMemo(() => [...DETAIL_BASE_COLUMNS, ...metadataColumns(results || [])], [results]);
  const rows    = useMemo(() => (results || []).map(r => ({
    id:         r.id,
    time:       convertToLocalTime(r.time),
    user_email: r.user_email,
    ...flattenMetadata(r.metadata),
  })), [results]);

  const changeRange = useCallback((range) => {
    setSearchParams(rangeToSearch(range), { replace: true });
    setPage(1);
  }, [setSearchParams]);

  const changePageSize = useCallback((size) => {
    setPageSize(size);
    setPage(1);
  }, []);

  return (
    <Animate>
      <Link to={`/product-events?${rangeToSearch(dateRange)}`} aria-label={`Back to ${MSG.backToList}`} className={Style.backLink}>
        <Icon image="chevron-left" size={14} />
        {MSG.backToList}
      </Link>

      <div className={Style.detailHeader}>
        <div>
          <h1 className={Style.detailTitle}>{name}</h1>
          <p className={Style.detailSubtitle}>
            {list.loading ? '\u00a0' : `${total} event${total === 1 ? '' : 's'} \u00b7 ${dateRange.start} to ${dateRange.end}`}
          </p>
        </div>
        <DateRangeSelector value={dateRange} onChange={changeRange} />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={list.loading}
        emptyMessage={MSG.detailEmpty}
        page={page}
        pageSize={pageSize}
        totalPages={Math.max(1, Math.ceil(total / pageSize))}
        onPageChange={setPage}
        onPageSizeChange={changePageSize}
        pageSizes={PAGE_SIZES}
        renderCell={(key, value) => {
          if (key === 'time')       return <td key={key} className={Style.cellTime}>{value || '\u2014'}</td>;
          if (key === 'user_email') return <td key={key} className={Style.cellUser}>{value || '\u2014'}</td>;
          if (isMetadataColumn(key)) {
            // free-text fields (e.g. reason_detail) get room to read; short codes stay compact
            const width = value?.length > LONG_VALUE ? Style.cellMetaLong : Style.cellMetaShort;
            return <td key={key} className={`${Style.cellMeta} ${width}`}>{value ?? '\u2014'}</td>;
          }
          return null;
        }}
      />
    </Animate>
  );
}
